import { FileStack, FileText, Globe, BookOpen, Ticket } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { AddSourceKind } from './add-sources-menu';

interface FirstSourceEmptyStateProps {
  onSelect: (kind: AddSourceKind) => void;
}

const FIRST_SOURCE_OPTIONS: Array<{ kind: AddSourceKind; icon: typeof FileText; title: string; description: string }> = [
  { kind: 'documents', icon: FileText, title: 'Documents', description: 'Upload PDFs, policy docs, product guides, or internal handbooks' },
  { kind: 'links', icon: Globe, title: 'Website links', description: 'Paste a URL and Kim will crawl and learn from your web pages' },
  { kind: 'article', icon: BookOpen, title: 'Create article', description: 'Write a support article directly in Kim — no file needed' },
  { kind: 'tickets', icon: Ticket, title: 'Previous tickets', description: 'Connect your helpdesk so Kim can learn from resolved conversations' }
];

// "Let's add your first source" — shown when the account has no sources and no SOPs at all.
export function FirstSourceEmptyState({ onSelect }: FirstSourceEmptyStateProps) {
  return (
    <div className='flex flex-1 items-center justify-center py-24'>
      <div className='w-full max-w-md flex flex-col gap-4'>
        <div>
          <h2 className='text-sm font-semibold'>Let&apos;s add your first source</h2>
          <p className='text-xs text-muted-foreground mt-1'>
            The more you connect, the smarter Kim gets. Start with whatever&apos;s easiest.
          </p>
        </div>
        <div className='flex flex-col gap-2'>
          {FIRST_SOURCE_OPTIONS.map(({ kind, icon: Icon, title, description }) => (
            <button
              key={kind}
              type='button'
              onClick={() => onSelect(kind)}
              className='flex items-start gap-3 rounded-lg border p-3 text-left hover:bg-muted/50 transition-colors cursor-pointer'
            >
              <Icon className='size-4 mt-0.5 text-muted-foreground shrink-0' />
              <div className='flex-1 min-w-0'>
                <p className='text-sm font-medium'>{title}</p>
                <p className='text-xs text-muted-foreground'>{description}</p>
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

interface NoOperatingProceduresEmptyStateProps {
  className?: string;
}

// "No protocol drafts yet" — shown on the SOP's tab once at least one source exists.
export function NoOperatingProceduresEmptyState({ className }: NoOperatingProceduresEmptyStateProps) {
  return (
    <div className={cn('flex flex-col items-center justify-center gap-3 py-24 text-center', className)}>
      <FileStack className='size-10 text-muted-foreground/40' strokeWidth={1.25} />
      <p className='text-sm font-semibold'>No drafts to review</p>
      <p className='text-xs text-gray-900 max-w-xs'>
        Draft protocols from your past tickets with Ticket Categorization, or upload a document from New Protocol.
      </p>
    </div>
  );
}
